import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/Login.css";
import { Form, Row, Col, Container, Spinner } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { signup } from "../../services/authService";
import PopupMessage from "../../components/common/PopupMessage";
import { faculties, facultyLevelsMap } from "../Activities/AddActivity.js";
import { useSound } from "../../hooks/useSound.js";

// --- Icons ---
const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
    <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="currentColor"
    viewBox="0 0 16 16"
  >
    <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
    <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
    <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
  </svg>
);

const Signup = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    faculty: "",
    level: "",
    gender: "",
    universtyID: "",
    phone: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({ show: false, type: "", message: "" });
  const [availableLevels, setAvailableLevels] = useState([]);
  const [showPassword, setShowPassword] = useState(false);

  const playSignUpSound = useSound("/sounds/success.mp3");
  const playSignUpSoundError = useSound("/sounds/error.mp3");

  // --- Logic Preserved ---
  const EmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const egyptPhoneRegex = /^01[0125][0-9]{8}$/;
  const universityIdRegex = /^\d{9}$/;

  const validateField = (name, value) => {
    let message = "";
    switch (name) {
      case "fullName":
        if (!value.trim()) {
          message = "الاسم بالكامل مطلوب | Full name is required";
        } else if (value.trim().length < 6) {
          message =
            "الاسم يجب أن يكون 6 أحرف على الأقل | Full name must be at least 6 characters";
        } else if (!value.trim().includes(" ")) {
          message =
            "الرجاء إدخال الاسم الأول والأخير | Please enter your first and last name";
        }
        break;
      case "email":
        if (!value) {
          message = "البريد الإلكتروني مطلوب | Email is required";
        }
        // التأكد من منع دومين الجامعة
        else if (value.toLowerCase().endsWith("@nub.edu.eg")) {
          message =
            "غير مسموح بالإيميل الجامعي | University emails are not allowed";
        } else if (!EmailRegex.test(value)) {
          message = "صيغة الإيميل غير صحيحة | Must be a valid email";
        }
        break;
      case "password":
        if (!value) message = "كلمة المرور مطلوبة | Password is required";
        else if (value.length < 6 || value.length > 30)
          message =
            "يجب أن تكون بين 6 و 30 حرف | Password must be between 6 and 30 characters";
        break;
      case "universtyID":
        if (!value) message = "الرقم الجامعي مطلوب | University ID is required";
        else if (!universityIdRegex.test(value))
          message =
            "يجب أن يتكون من 9 أرقام | University ID must be 9 digits (e.g. 221050193)";
        break;
      case "phone":
        if (!value) message = "رقم الهاتف مطلوب | Phone number is required";
        else if (!egyptPhoneRegex.test(value))
          message = "رقم هاتف مصري غير صحيح | Invalid Egyptian phone number";
        break;
      case "faculty":
        if (!value) message = "الكلية مطلوبة | Faculty is required";
        break;
      case "level":
        if (!value) message = "المستوى الدراسي مطلوب | Level is required";
        break;
      case "gender":
        if (!value) message = "تحديد الجنس مطلوب | Gender is required";
        break;
      default:
        break;
    }
    setErrors((prev) => ({ ...prev, [name]: message }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "faculty" ? { level: "" } : {}),
    }));

    validateField(name, value);

    if (name === "faculty") {
      const years = facultyLevelsMap[value] || 0;
      const newLevels = Array.from(
        { length: years },
        (_, i) => `LEVEL_${i + 1}`,
      );
      setAvailableLevels(newLevels);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      validateField(key, formData[key]);
      if (!formData[key])
        newErrors[key] = "هذا الحقل مطلوب | This field is required";
    });

    if (
      Object.values(errors).some((err) => err) ||
      Object.keys(newErrors).length > 0
    ) {
      setPopup({
        show: true,
        type: "error",
        message:
          "برجاء مراجعة الأخطاء قبل المتابعة | Please fix the errors before submitting",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await signup(formData);
      setPopup({
        show: true,
        type: "success",
        message:
          response?.message ||
          "تم التسجيل بنجاح! يرجى تأكيد بريدك. | Signup successful! Please verify your email.",
      });
      playSignUpSound();
      setFormData({
        fullName: "",
        email: "",
        password: "",
        faculty: "",
        level: "",
        gender: "",
        universtyID: "",
        phone: "",
      });

      setTimeout(() => {
        navigate("/auth/EmailVerification", {
          state: { email: formData.email },
        });
      }, 2000);
    } catch (error) {
      console.log(error);
      let popupMessage =
        "فشل التسجيل. يرجى المحاولة مرة أخرى. | Signup failed. Please try again.";
      if (error?.response?.data?.errors) {
        const errorsObj = error.response.data?.errors;
        popupMessage = Object.values(errorsObj).flat().join(" , ");
      } else if (error?.response?.data?.message) {
        popupMessage = error.response.data?.message;
      }
      playSignUpSoundError();

      setPopup({
        show: true,
        type: "error",
        message: popupMessage,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="background-wrapper">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
      </div>

      <div className="login-advanced-container" style={{ paddingTop: "70px" }}>
        <Container fluid="md" className="p-0">
          <Row className="justify-content-center m-0">
            <Col xs={12} lg={10} xl={9} className="p-0">
              <div className="login-card d-flex flex-column flex-md-row shadow-lg">
                {/* Brand Section (Left) */}
                <Col
                  md={5}
                  className="brand-section d-none d-md-flex text-white"
                >
                  <div
                    style={{
                      position: "relative",
                      zIndex: 2,
                      textAlign: "center",
                      margin: "auto",
                    }}
                  >
                    <img
                      src="/CampusPulsLogo.png"
                      alt="CampusPulse Logo"
                      style={{ width: "100px", marginBottom: "2rem" }}
                    />
                    <h2 className="fw-bold mb-2" dir="rtl">
                      انضم إلينا
                    </h2>
                    <h4
                      className="fw-bold mb-3"
                      style={{ fontSize: "1.1rem", color: "#94a3b8" }}
                      dir="ltr"
                    >
                      Join Us
                    </h4>
                    <p className="mb-1" style={{ color: "#f8fafc" }} dir="rtl">
                      أنشئ حسابك الآن لتتواصل مع مجتمعك الجامعي.
                    </p>
                    <p
                      style={{ fontSize: "0.9rem", color: "#94a3b8" }}
                      dir="ltr"
                    >
                      Create an account to connect with your university
                      community.
                    </p>
                  </div>
                </Col>

                {/* Form Section (Right) */}
                <Col
                  xs={12}
                  md={7}
                  className="form-section bg-transparent p-4 p-md-5"
                >
                  {/* Mobile Logo */}
                  <div className="text-center d-md-none mobile-logo mb-4">
                    <img
                      src="/CampusPulsLogo.png"
                      alt="Logo"
                      style={{ width: "80px" }}
                    />
                  </div>

                  <div className="mb-4 mb-md-5 d-flex flex-column gap-2">
                    <div className="d-flex flex-column flex-md-row align-items-center justify-content-center justify-content-md-between gap-1">
                      <h3 className="fw-bold text-white mb-0" dir="rtl">
                        إنشاء حساب
                      </h3>
                      <h4
                        className="fw-bold mb-0 d-none d-md-block"
                        dir="ltr"
                        style={{ fontSize: "1.3rem", color: "#cbd5e1" }}
                      >
                        Sign Up
                      </h4>
                    </div>
                    <div className="d-flex flex-column align-items-center align-items-md-start gap-1">
                      <p
                        className="small m-0"
                        style={{ color: "#f8fafc" }}
                        dir="rtl"
                      >
                        أدخل بياناتك للبدء في التسجيل.
                      </p>
                      <p
                        className="small m-0"
                        style={{ color: "#94a3b8" }}
                        dir="ltr"
                      >
                        Fill in the details to get started.
                      </p>
                    </div>
                  </div>

                  {/* 1. Added autoComplete="off" to Form to discourage autofill */}
                  <Form onSubmit={handleSubmit} autoComplete="off">
                    {/* Full Name */}
                    <div className="input-group-advanced mb-3">
                      <label className="input-label d-flex justify-content-between align-items-center w-100 px-1 mb-1">
                        <span
                          dir="ltr"
                          style={{
                            color: "#94a3b8",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          Full Name
                        </span>
                        <span
                          dir="rtl"
                          className="fw-bold"
                          style={{ color: "#f8fafc", fontSize: "0.95rem" }}
                        >
                          الاسم بالكامل
                        </span>
                      </label>
                      <input
                        type="text"
                        name="fullName"
                        className={`advanced-input ${errors.fullName ? "is-invalid" : ""}`}
                        placeholder="Ahmed Hmada Kamal"
                        value={formData.fullName}
                        onChange={handleChange}
                        disabled={loading}
                        autoComplete="off"
                      />
                      {errors.fullName && (
                        <small className="text-danger mt-1 d-block text-center fw-medium">
                          {errors.fullName}
                        </small>
                      )}
                    </div>

                    {/* Email */}
                    <div className="input-group-advanced mb-3">
                      <label className="input-label d-flex justify-content-between align-items-center w-100 px-1 mb-1">
                        <span
                          dir="ltr"
                          style={{
                            color: "#94a3b8",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          Email Address
                        </span>
                        <span
                          dir="rtl"
                          className="fw-bold"
                          style={{ color: "#f8fafc", fontSize: "0.95rem" }}
                        >
                          البريد الإلكتروني
                        </span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        dir="ltr"
                        className={`advanced-input ${errors.email ? "is-invalid" : ""}`}
                        placeholder="student@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={loading}
                        autoComplete="off"
                      />
                      {errors.email && (
                        <small className="text-danger mt-1 d-block text-center fw-medium">
                          {errors.email}
                        </small>
                      )}
                    </div>

                    {/* Password */}
                    <div className="input-group-advanced mb-3">
                      <label className="input-label d-flex justify-content-between align-items-center w-100 px-1 mb-1">
                        <span
                          dir="ltr"
                          style={{
                            color: "#94a3b8",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          Password
                        </span>
                        <span
                          dir="rtl"
                          className="fw-bold"
                          style={{ color: "#f8fafc", fontSize: "0.95rem" }}
                        >
                          كلمة المرور
                        </span>
                      </label>
                      <div className="input-wrapper-relative">
                        <input
                          type={showPassword ? "text" : "password"}
                          name="password"
                          dir="ltr"
                          className={`advanced-input with-icon ${errors.password ? "is-invalid" : ""}`}
                          placeholder="••••••••"
                          value={formData.password}
                          onChange={handleChange}
                          disabled={loading}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="Toggle password visibility"
                        >
                          {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                      </div>
                      {errors.password && (
                        <small className="text-danger mt-1 d-block text-center fw-medium">
                          {errors.password}
                        </small>
                      )}
                    </div>

                    {/* Faculty & Level (Grid) */}
                    <Row className="mb-3 g-3">
                      <Col md={6}>
                        <div className="input-group-advanced m-0">
                          <label className="input-label d-flex justify-content-between align-items-center w-100 px-1 mb-1">
                            <span
                              dir="ltr"
                              style={{
                                color: "#94a3b8",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                              }}
                            >
                              Faculty
                            </span>
                            <span
                              dir="rtl"
                              className="fw-bold"
                              style={{ color: "#f8fafc", fontSize: "0.95rem" }}
                            >
                              الكلية
                            </span>
                          </label>
                          <select
                            name="faculty"
                            dir="ltr"
                            className={`advanced-input ${errors.faculty ? "is-invalid" : ""}`}
                            value={formData.faculty}
                            onChange={handleChange}
                            disabled={loading}
                          >
                            <option value="">Select | اختر...</option>
                            {Object.entries(faculties)
                              .filter(([key]) => key !== "all")
                              .map(([key, value]) => (
                                <option key={key} value={value}>
                                  {value}
                                </option>
                              ))}
                          </select>
                          {errors.faculty && (
                            <small className="text-danger mt-1 d-block text-center fw-medium">
                              {errors.faculty}
                            </small>
                          )}
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="input-group-advanced m-0">
                          <label className="input-label d-flex justify-content-between align-items-center w-100 px-1 mb-1">
                            <span
                              dir="ltr"
                              style={{
                                color: "#94a3b8",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                              }}
                            >
                              Level / Year
                            </span>
                            <span
                              dir="rtl"
                              className="fw-bold"
                              style={{ color: "#f8fafc", fontSize: "0.95rem" }}
                            >
                              المستوى
                            </span>
                          </label>
                          <select
                            name="level"
                            dir="ltr"
                            className={`advanced-input ${errors.level ? "is-invalid" : ""}`}
                            value={formData.level}
                            onChange={handleChange}
                            disabled={loading}
                          >
                            <option value="">Select | اختر...</option>
                            {availableLevels.map((level) => (
                              <option key={level} value={level}>
                                {level}
                              </option>
                            ))}
                          </select>
                          {errors.level && (
                            <small className="text-danger mt-1 d-block text-center fw-medium">
                              {errors.level}
                            </small>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Gender & ID (Grid) */}
                    <Row className="mb-3 g-3">
                      <Col md={6}>
                        <div className="input-group-advanced m-0">
                          <label className="input-label d-flex justify-content-between align-items-center w-100 px-1 mb-1">
                            <span
                              dir="ltr"
                              style={{
                                color: "#94a3b8",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                              }}
                            >
                              Gender
                            </span>
                            <span
                              dir="rtl"
                              className="fw-bold"
                              style={{ color: "#f8fafc", fontSize: "0.95rem" }}
                            >
                              الجنس
                            </span>
                          </label>
                          <select
                            name="gender"
                            dir="ltr"
                            className={`advanced-input ${errors.gender ? "is-invalid" : ""}`}
                            value={formData.gender}
                            onChange={handleChange}
                            disabled={loading}
                          >
                            <option value="">Select | اختر...</option>
                            <option value="MALE">Male | ذكر</option>
                            <option value="FEMALE">Female | أنثى</option>
                          </select>
                          {errors.gender && (
                            <small className="text-danger mt-1 d-block text-center fw-medium">
                              {errors.gender}
                            </small>
                          )}
                        </div>
                      </Col>

                      <Col md={6}>
                        <div className="input-group-advanced m-0">
                          <label className="input-label d-flex justify-content-between align-items-center w-100 px-1 mb-1">
                            <span
                              dir="ltr"
                              style={{
                                color: "#94a3b8",
                                fontSize: "0.85rem",
                                fontWeight: "600",
                              }}
                            >
                              University ID
                            </span>
                            <span
                              dir="rtl"
                              className="fw-bold"
                              style={{ color: "#f8fafc", fontSize: "0.95rem" }}
                            >
                              الرقم الجامعي
                            </span>
                          </label>
                          <input
                            type="text"
                            name="universtyID"
                            dir="ltr"
                            className={`advanced-input ${errors.universtyID ? "is-invalid" : ""}`}
                            placeholder="e.g. 221050193"
                            value={formData.universtyID}
                            onChange={handleChange}
                            disabled={loading}
                            autoComplete="off"
                          />
                          {errors.universtyID && (
                            <small className="text-danger mt-1 d-block text-center fw-medium">
                              {errors.universtyID}
                            </small>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Phone */}
                    <div className="input-group-advanced mb-4">
                      <label className="input-label d-flex justify-content-between align-items-center w-100 px-1 mb-1">
                        <span
                          dir="ltr"
                          style={{
                            color: "#94a3b8",
                            fontSize: "0.85rem",
                            fontWeight: "600",
                          }}
                        >
                          Phone Number
                        </span>
                        <span
                          dir="rtl"
                          className="fw-bold"
                          style={{ color: "#f8fafc", fontSize: "0.95rem" }}
                        >
                          رقم الموبايل
                        </span>
                      </label>
                      <input
                        type="text"
                        name="phone"
                        dir="ltr"
                        className={`advanced-input ${errors.phone ? "is-invalid" : ""}`}
                        placeholder="e.g. 01012345678"
                        value={formData.phone}
                        onChange={handleChange}
                        disabled={loading}
                        autoComplete="off"
                      />
                      {errors.phone && (
                        <small className="text-danger mt-1 d-block text-center fw-medium">
                          {errors.phone}
                        </small>
                      )}
                    </div>

                    {/* Submit Button */}
                    <button
                      type="submit"
                      className="btn-primary-advanced d-flex flex-column align-items-center justify-content-center gap-1 w-100 py-3 mt-2"
                      disabled={loading}
                    >
                      {loading ? (
                        <div className="d-flex align-items-center gap-2">
                          <Spinner as="span" animation="border" size="sm" />
                          <span className="fw-bold">
                            جاري إنشاء الحساب... | Processing...
                          </span>
                        </div>
                      ) : (
                        <>
                          <span
                            style={{ fontWeight: "700", fontSize: "1.1rem" }}
                          >
                            إنشاء حساب
                          </span>
                          <span style={{ fontSize: "0.85rem", opacity: 0.8 }}>
                            Sign Up
                          </span>
                        </>
                      )}
                    </button>

                    <div className="links-container d-flex flex-column flex-sm-row justify-content-center align-items-center mt-4 gap-2">
                      <span
                        className="text-center"
                        style={{ color: "#cbd5e1", fontSize: "0.95rem" }}
                      >
                        لديك حساب بالفعل؟ | Already have an account?
                      </span>
                      <Link
                        to="/auth/login"
                        className="link-advanced link-highlight fw-bold m-0"
                        style={{ fontSize: "1rem" }}
                      >
                        تسجيل الدخول | Log In
                      </Link>
                    </div>
                  </Form>
                </Col>
              </div>
            </Col>
          </Row>
        </Container>

        {popup.show && (
          <PopupMessage
            type={popup.type}
            message={popup.message}
            onClose={() => setPopup({ ...popup, show: false })}
          />
        )}
      </div>
    </>
  );
};

export default Signup;

// import "bootstrap/dist/css/bootstrap.min.css";
// import "../../assets/styles/Login.css";
// import { Form, Row, Col, Container, Spinner } from "react-bootstrap";
// import { Link, useNavigate } from "react-router-dom";
// import { useState } from "react";
// import { signup } from "../../services/authService";
// import PopupMessage from "../../components/common/PopupMessage";
// import { faculties, facultyLevelsMap } from "../Activities/AddActivity.js";
// import { useSound } from "../../hooks/useSound.js";

// // --- Icons ---
// const EyeIcon = () => (
//   <svg
//     xmlns="http://www.w3.org/2000/svg"
//     width="20"
//     height="20"
//     fill="currentColor"
//     viewBox="0 0 16 16"
//   >
//     <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z" />
//     <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z" />
//   </svg>
// );

// const EyeSlashIcon = () => (
//   <svg
//     xmlns="http://www.w3.org/2000/svg"
//     width="20"
//     height="20"
//     fill="currentColor"
//     viewBox="0 0 16 16"
//   >
//     <path d="M13.359 11.238C15.06 9.72 16 8 16 8s-3-5.5-8-5.5a7.028 7.028 0 0 0-2.79.588l.77.771A5.944 5.944 0 0 1 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.134 13.134 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755-.165.165-.337.328-.517.486l.708.709z" />
//     <path d="M11.297 9.176a3.5 3.5 0 0 0-4.474-4.474l.823.823a2.5 2.5 0 0 1 2.829 2.829l.822.822zm-2.943 1.299.822.822a3.5 3.5 0 0 1-4.474-4.474l.823.823a2.5 2.5 0 0 0 2.829 2.829z" />
//     <path d="M3.35 5.47c-.18.16-.353.322-.518.487A13.134 13.134 0 0 0 1.172 8l.195.288c.335.48.83 1.12 1.465 1.755C4.121 11.332 5.881 12.5 8 12.5c.716 0 1.39-.133 2.02-.36l.77.772A7.029 7.029 0 0 1 8 13.5C3 13.5 0 8 0 8s.939-1.721 2.641-3.238l.708.709zm10.296 8.884-12-12 .708-.708 12 12-.708.708z" />
//   </svg>
// );

// const Signup = () => {
//   const navigate = useNavigate();
//   const [formData, setFormData] = useState({
//     fullName: "",
//     email: "",
//     password: "",
//     faculty: "",
//     level: "",
//     gender: "",
//     universtyID: "",
//     phone: "",
//   });

//   const [errors, setErrors] = useState({});
//   const [loading, setLoading] = useState(false);
//   const [popup, setPopup] = useState({ show: false, type: "", message: "" });
//   const [availableLevels, setAvailableLevels] = useState([]);
//   const [showPassword, setShowPassword] = useState(false);

//   const playSignUpSound = useSound("/sounds/success.mp3");
//   const playSignUpSoundError = useSound("/sounds/error.mp3");

//   // --- Logic Preserved ---
//   const EmailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
//   const egyptPhoneRegex = /^01[0125][0-9]{8}$/;
//   const universityIdRegex = /^\d{9}$/;

//   const validateField = (name, value) => {
//     let message = "";
//     switch (name) {
//       case "fullName":
//         if (!value.trim()) {
//           message = "Full name is required";
//         } else if (value.trim().length < 6) {
//           message = "Full name must be at least 6 characters";
//         } else if (!value.trim().includes(" ")) {
//           message = "Please enter your first and last name";
//         }
//         break;
//       // case "email":
//       //   if (!value) message = "Email is required";
//       //   else if (!EmailRegex.test(value)) message = "Must be a valid email";
//       //   break;
//       case "email":
//         if (!value) {
//           message = "Email is required";
//         }
//         // التأكد من منع دومين الجامعة
//         else if (value.toLowerCase().endsWith("@nub.edu.eg")) {
//           message = "University emails are not allowed";
//         } else if (!EmailRegex.test(value)) {
//           message = "Must be a valid email";
//         }
//         break;
//       case "password":
//         if (!value) message = "Password is required";
//         else if (value.length < 6 || value.length > 30)
//           message = "Password must be between 6 and 30 characters";
//         break;
//       case "universtyID":
//         if (!value) message = "University ID is required";
//         else if (!universityIdRegex.test(value))
//           message = "University ID must be 9 digits (e.g. 221050193)";
//         break;
//       case "phone":
//         if (!value) message = "Phone number is required";
//         else if (!egyptPhoneRegex.test(value))
//           message = "Invalid Egyptian phone number";
//         break;
//       case "faculty":
//         if (!value) message = "Faculty is required";
//         break;
//       case "level":
//         if (!value) message = "Level is required";
//         break;
//       case "gender":
//         if (!value) message = "Gender is required";
//         break;
//       default:
//         break;
//     }
//     setErrors((prev) => ({ ...prev, [name]: message }));
//   };

//   const handleChange = (e) => {
//     const { name, value } = e.target;
//     setFormData((prev) => ({
//       ...prev,
//       [name]: value,
//       ...(name === "faculty" ? { level: "" } : {}),
//     }));

//     validateField(name, value);

//     if (name === "faculty") {
//       const years = facultyLevelsMap[value] || 0;
//       const newLevels = Array.from(
//         { length: years },
//         (_, i) => `LEVEL_${i + 1}`,
//       );
//       setAvailableLevels(newLevels);
//     }
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     const newErrors = {};
//     Object.keys(formData).forEach((key) => {
//       validateField(key, formData[key]);
//       if (!formData[key]) newErrors[key] = `${key} is required`;
//     });

//     if (
//       Object.values(errors).some((err) => err) ||
//       Object.keys(newErrors).length > 0
//     ) {
//       setPopup({
//         show: true,
//         type: "error",
//         message: "Please fix the errors before submitting",
//       });
//       return;
//     }

//     setLoading(true);

//     try {
//       const response = await signup(formData);
//       setPopup({
//         show: true,
//         type: "success",
//         message:
//           response?.message || "Signup successful! Please verify your email.",
//       });
//       playSignUpSound();
//       setFormData({
//         fullName: "",
//         email: "",
//         password: "",
//         faculty: "",
//         level: "",
//         gender: "",
//         universtyID: "",
//         phone: "",
//       });

//       setTimeout(() => {
//         navigate("/auth/EmailVerification", {
//           state: { email: formData.email },
//         });
//       }, 2000);
//     } catch (error) {
//       console.log(error);
//       let popupMessage = "Signup failed. Please try again.";
//       if (error?.response?.data?.errors) {
//         const errorsObj = error.response.data?.errors;
//         popupMessage = Object.values(errorsObj).flat().join(" , ");
//       } else if (error?.response?.data?.message) {
//         popupMessage = error.response.data?.message;
//       }
//       playSignUpSoundError();

//       setPopup({
//         show: true,
//         type: "error",
//         message: popupMessage,
//       });
//     } finally {
//       setLoading(false);
//     }
//   };

//   return (
//     <>
//       <div className="background-wrapper">
//         <div className="orb orb-1"></div>
//         <div className="orb orb-2"></div>
//       </div>

//       <div className="login-advanced-container" style={{ paddingTop: "70px" }}>
//         <Container fluid="md" className="p-0">
//           <Row className="justify-content-center m-0">
//             <Col xs={12} lg={10} xl={9} className="p-0">
//               <div className="login-card d-flex flex-column flex-md-row">
//                 {/* Brand Section (Left) */}
//                 <Col
//                   md={5}
//                   className="brand-section d-none d-md-flex text-white"
//                 >
//                   <div
//                     style={{
//                       position: "relative",
//                       zIndex: 2,
//                       textAlign: "center",
//                     }}
//                   >
//                     <img
//                       src="/CampusPulsLogo.png"
//                       alt="CampusPulse Logo"
//                       style={{ width: "100px", marginBottom: "2rem" }}
//                     />
//                     <h2 className="fw-bold mb-3">Join Us</h2>
//                     <p className="text-white-50">
//                       Create an account to connect with your university
//                       community.
//                     </p>
//                   </div>
//                 </Col>

//                 {/* Form Section (Right) */}
//                 <Col xs={12} md={7} className="form-section bg-transparent">
//                   {/* Mobile Logo */}
//                   <div className="text-center d-md-none mobile-logo">
//                     <img
//                       src="/CampusPulsLogo.png"
//                       alt="Logo"
//                       style={{ width: "80px" }}
//                     />
//                   </div>

//                   <div className="mb-4">
//                     <h3 className="fw-bold text-white mb-1">Sign Up</h3>
//                     <p className="text-secondary small">
//                       Fill in the details to get started.
//                     </p>
//                   </div>

//                   {/* 1. Added autoComplete="off" to Form to discourage autofill */}
//                   <Form onSubmit={handleSubmit} autoComplete="off">
//                     {/* Full Name */}
//                     <div className="input-group-advanced">
//                       <label className="input-label">Full Name</label>
//                       <input
//                         type="text"
//                         name="fullName"
//                         className={`advanced-input ${errors.fullName ? "is-invalid" : ""}`}
//                         placeholder="e.g. Ahmed Hmada Kamal"
//                         value={formData.fullName}
//                         onChange={handleChange}
//                         disabled={loading}
//                         // 2. Added explicit autocomplete off
//                         autoComplete="off"
//                       />
//                       {errors.fullName && (
//                         <small className="text-danger mt-1 d-block">
//                           {errors.fullName}
//                         </small>
//                       )}
//                     </div>

//                     {/* Email */}
//                     <div className="input-group-advanced">
//                       <label className="input-label">Email Address</label>
//                       <input
//                         type="email"
//                         name="email"
//                         className={`advanced-input ${errors.email ? "is-invalid" : ""}`}
//                         placeholder="student@example.com"
//                         value={formData.email}
//                         onChange={handleChange}
//                         disabled={loading}
//                         // 3. Added explicit autocomplete off
//                         autoComplete="off"
//                       />
//                       {errors.email && (
//                         <small className="text-danger mt-1 d-block">
//                           {errors.email}
//                         </small>
//                       )}
//                     </div>

//                     {/* Password */}
//                     <div className="input-group-advanced">
//                       <label className="input-label">Password</label>
//                       <div className="input-wrapper-relative">
//                         <input
//                           type={showPassword ? "text" : "password"}
//                           name="password"
//                           className={`advanced-input with-icon ${errors.password ? "is-invalid" : ""}`}
//                           placeholder="••••••••"
//                           value={formData.password}
//                           onChange={handleChange}
//                           disabled={loading}
//                           // 4. THE FIX: new-password prevents browser from using saved passwords
//                           autoComplete="new-password"
//                         />
//                         <button
//                           type="button"
//                           className="password-toggle"
//                           onClick={() => setShowPassword(!showPassword)}
//                           aria-label="Toggle password visibility"
//                         >
//                           {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
//                         </button>
//                       </div>
//                       {errors.password && (
//                         <small className="text-danger mt-1 d-block">
//                           {errors.password}
//                         </small>
//                       )}
//                     </div>

//                     {/* Faculty & Level (Grid) */}
//                     <Row>
//                       <Col md={6}>
//                         <div className="input-group-advanced">
//                           <label className="input-label">Faculty</label>
//                           <select
//                             name="faculty"
//                             className={`advanced-input ${errors.faculty ? "is-invalid" : ""}`}
//                             value={formData.faculty}
//                             onChange={handleChange}
//                             disabled={loading}
//                           >
//                             <option value="">Select Faculty</option>
//                             {Object.entries(faculties)
//                               .filter(([key]) => key !== "all")
//                               .map(([key, value]) => (
//                                 <option key={key} value={value}>
//                                   {value}
//                                 </option>
//                               ))}
//                           </select>
//                           {errors.faculty && (
//                             <small className="text-danger mt-1 d-block">
//                               {errors.faculty}
//                             </small>
//                           )}
//                         </div>
//                       </Col>

//                       <Col md={6}>
//                         <div className="input-group-advanced">
//                           <label className="input-label">Level / Year</label>
//                           <select
//                             name="level"
//                             className={`advanced-input ${errors.level ? "is-invalid" : ""}`}
//                             value={formData.level}
//                             onChange={handleChange}
//                             disabled={loading}
//                           >
//                             <option value="">Select Level</option>
//                             {availableLevels.map((level) => (
//                               <option key={level} value={level}>
//                                 {level}
//                               </option>
//                             ))}
//                           </select>
//                           {errors.level && (
//                             <small className="text-danger mt-1 d-block">
//                               {errors.level}
//                             </small>
//                           )}
//                         </div>
//                       </Col>
//                     </Row>

//                     {/* Gender & ID (Grid) */}
//                     <Row>
//                       <Col md={6}>
//                         <div className="input-group-advanced">
//                           <label className="input-label">Gender</label>
//                           <select
//                             name="gender"
//                             className={`advanced-input ${errors.gender ? "is-invalid" : ""}`}
//                             value={formData.gender}
//                             onChange={handleChange}
//                             disabled={loading}
//                           >
//                             <option value="">Select Gender</option>
//                             <option value="MALE">Male</option>
//                             <option value="FEMALE">Female</option>
//                           </select>
//                           {errors.gender && (
//                             <small className="text-danger mt-1 d-block">
//                               {errors.gender}
//                             </small>
//                           )}
//                         </div>
//                       </Col>

//                       <Col md={6}>
//                         <div className="input-group-advanced">
//                           <label className="input-label">University ID</label>
//                           <input
//                             type="text"
//                             name="universtyID"
//                             className={`advanced-input ${errors.universtyID ? "is-invalid" : ""}`}
//                             placeholder="e.g. 221050193"
//                             value={formData.universtyID}
//                             onChange={handleChange}
//                             disabled={loading}
//                             autoComplete="off"
//                           />
//                           {errors.universtyID && (
//                             <small className="text-danger mt-1 d-block">
//                               {errors.universtyID}
//                             </small>
//                           )}
//                         </div>
//                       </Col>
//                     </Row>

//                     {/* Phone */}
//                     <div className="input-group-advanced">
//                       <label className="input-label">Phone Number</label>
//                       <input
//                         type="text"
//                         name="phone"
//                         className={`advanced-input ${errors.phone ? "is-invalid" : ""}`}
//                         placeholder="e.g. 01012345678"
//                         value={formData.phone}
//                         onChange={handleChange}
//                         disabled={loading}
//                         autoComplete="off"
//                       />
//                       {errors.phone && (
//                         <small className="text-danger mt-1 d-block">
//                           {errors.phone}
//                         </small>
//                       )}
//                     </div>

//                     {/* Submit Button */}
//                     <button
//                       type="submit"
//                       className="btn-primary-advanced mt-2"
//                       disabled={loading}
//                     >
//                       {loading ? (
//                         <span>
//                           <Spinner
//                             as="span"
//                             animation="border"
//                             size="sm"
//                             className="me-2"
//                           />{" "}
//                           Creating Account...
//                         </span>
//                       ) : (
//                         "Sign Up"
//                       )}
//                     </button>

//                     <div className="links-container justify-content-center mt-4">
//                       <span className="text-secondary me-2">
//                         Already have an account?
//                       </span>
//                       <Link
//                         to="/auth/login"
//                         className="link-advanced link-highlight"
//                       >
//                         Log In
//                       </Link>
//                     </div>
//                   </Form>
//                 </Col>
//               </div>
//             </Col>
//           </Row>
//         </Container>

//         {popup.show && (
//           <PopupMessage
//             type={popup.type}
//             message={popup.message}
//             onClose={() => setPopup({ ...popup, show: false })}
//           />
//         )}
//       </div>
//     </>
//   );
// };

// export default Signup;
